import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';

import { SearchRoundIcon } from '../../../../constants';
import CustomSelect from '../../../CustomSelect/CustomSelect';
import { unitedStates } from '../../../Utils/helper';
import { Style, customSelectStyle } from './Style';
import Buttons from '../Buttons';
import VehicleInfo from '../VehicleInfo';

import Debounce from '../../../../utils/debounce';
import { LookupApi } from '../../../../utils/vin-api';
import { Toast } from '../../../Toast/Toast';
import VehicleFields from '../VehicleFields';

const useStyles = makeStyles(() => Style());

export default function PlateLookup({ setToggle }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [plateLoader, setPlateLoader] = useState(false);
  const [states, setStates] = useState([]);
  const [unitedState, setUnitedState] = useState('');
  const [showPlateSearchVehicles, setShowPlateSearchVehicles] = useState(false);
  const [plateSearchVehicles, setPlateSearchVehicles] = useState([]);

  const [plateSearchValue, setPlateSearchValue] = useState('');

  const selectedVehicle = useSelector(
    (state) =>
      state.customerVehicleSearch?.selectedCustomerAndVehicle?.vehicle || '',
  );

  const createVehicleLoading = useSelector(
    (state) => state.vehicle?.createVehicleLoading || false,
  );

  const generateROLoading = useSelector(
    (state) => state?.ro?.generateROLoading || false,
  );
  const pathname = history.location?.pathname || '';

  const [vehicle, setVehicle] = useState({
    displacement: '',
    engineDisplacementType: 'Liters',
  });

  useEffect(() => {
    setStates(unitedStates()?.map((item) => ({ ...item, name: item?.label })));
  }, []);

  // handle Plate data
  const handlePlateData = async (plate = '', lookupData = '') => {
    if (plate) {
      try {
        const { data: { vinData = [] } = '' } = lookupData || '';

        if (vinData?.length && plate) {
          vinData.map((d) => {
            d.plate = plate;
            d.vin = d.vin || '';
            return d;
          });
        }
        setShowPlateSearchVehicles('plate');

        setPlateSearchVehicles(vinData);
      } catch (error) {
        const err = 'Invalid Plate';
        Toast('error', error?.response?.data?.message || err);

        setPlateLoader(false);
        return error;
      }
    }
  };

  const handleChangePlate = useCallback(Debounce(handlePlateData), []);

  const clearVehicleInfoOnHeader = (payload = '') => {
    if (
      !(
        selectedVehicle?.id &&
        (pathname.includes('/estimator') ||
          pathname.includes('/new-repairorder'))
      )
    ) {
      dispatch({ type: 'SET_VEHICLE_INFO_ON_HEADER', payload });
    }
  };

  // plate lookup API
  const callPlateLookupApi = async (plateNum, usState) => {
    if (!plateNum || !usState) return;

    try {
      setPlateSearchVehicles([]);
      setVehicle();
      clearVehicleInfoOnHeader();

      setPlateLoader(true);

      const result = await LookupApi(plateNum, usState);

      setPlateLoader(false);

      if (result) {
        handleChangePlate(plateNum, result);
      }
    } catch (error) {
      const err = 'Invalid Plate Lookup number';
      Toast('error', err);
      setPlateLoader(false);

      return err;
    }
  };

  const handlePlateLookup = useCallback(Debounce(callPlateLookupApi), []);

  const onSelectVehicle = (plateData = '') => {
    if (plateData) {
      let displacement = '';
      let displacementType = 'Liters';
      let transmissionStyle = '';

      const { DisplacementCC = '', TransmissionStyle = '' } = plateData || '';

      let { DisplacementL = '' } = plateData || '';

      DisplacementL = DisplacementL?.replace('L', '') || '';

      if (DisplacementL) {
        displacement = DisplacementL;
        displacementType = 'Liters';
      } else if (DisplacementCC) {
        displacement = DisplacementCC;
        displacementType = 'CC';
      }

      if (TransmissionStyle?.toLowerCase()?.includes('manual')) {
        transmissionStyle = 'Manual';
      } else if (TransmissionStyle?.toLowerCase()?.includes('automatic')) {
        transmissionStyle = 'Auto';
      }

      const vehicleInfo =
        plateData?.DisplayName ||
        plateData?.ModelYear + ' ' + plateData?.Make + ' ' + plateData?.Model;

      setVehicle({
        ...vehicle,
        vehicleType: plateData?.VehicleType || '',
        noOfCylinder: plateData?.EngineCylinders || '',
        vin: plateData?.vin || '',
        fuelType: plateData?.FuelTypePrimary || '',
        vehicleMake: plateData?.Make || '',
        vehicleModel: plateData?.Model || '',
        modelYear: plateData?.ModelYear || '',
        notes: plateData?.Note || '',
        licensePlate: plateData?.plate || '',
        displacement: displacement || null,
        engineDisplacementType: displacementType,
        transmission: transmissionStyle,
        submodel: plateData?.Submodel,
        baseVehicleId: plateData?.baseVehicleId || '',
        engineId: plateData?.engineId || '',
        mcNumber: plateData?.mcNumber || '',
        unitNumber: plateData?.unitNumber || '',
        vehicleInfo,
      });

      clearVehicleInfoOnHeader(vehicleInfo);
    }
    setShowPlateSearchVehicles(false);
  };

  const clearState = () => {
    setPlateLoader(false);
    setUnitedState('');
    setShowPlateSearchVehicles(false);
    setPlateSearchVehicles([]);
    setPlateSearchValue('');
    setVehicle({ displacement: null, engineDisplacementType: 'Liters' });
    clearVehicleInfoOnHeader();
  };

  const vehicleDetails = () => {
    delete vehicle.vehicleInfo;

    return vehicle;
  };
  const handleChangeUnitNumber = (e) => {
    const { value } = e.target;
    setVehicle({
      ...vehicle,
      unitNumber: value,
    });
  };

  const handleChangeMcNumber = (e) => {
    const { value } = e.target;
    setVehicle({
      ...vehicle,
      mcNumber: value,
    });
  };
  return (
    <div>
      <VehicleInfo
        vehicleInfo={vehicle?.vehicleInfo || ''}
        onClear={clearState}
        disabled={plateLoader || createVehicleLoading || generateROLoading}
      />

      {/* Plate LookUp */}

      <div className={classes.flex__bw}>
        <div className={classes.texts__side}>
          <Typography className={classes.heading}>
            License Plate Lookup
          </Typography>
          <Typography className={classes.para}>
            Enter License Plate Number and State to search vehicle
          </Typography>
        </div>
        <div className={clsx('d-flex p-relative', classes.width__fields)}>
          <TextField
            type='text'
            name='search'
            placeholder='Enter License Plate'
            value={plateSearchValue}
            fullWidth
            InputProps={{
              maxlength: 15,
              min: 1,
              endAdornment: plateLoader && <CircularProgress size={12} />,
              disableUnderline: true,
            }}
            disabled={plateLoader}
            onChange={(e) => {
              setPlateSearchValue(e.target.value?.trimStart() || '');
            }}
            className={clsx(classes.data__field, classes.width__w__state)}
          />
          <div className={classes.w__state}>
            <CustomSelect
              vehicleSearch
              testId='stateDropdown'
              disabled={plateLoader}
              menuItems={states}
              value={unitedState || 'State'}
              handleChange={(option) => {
                setUnitedState(option?.value);
              }}
              haveSearchInput
              customStyle={{ ...customSelectStyle }}
              plateLookUp
            />
          </div>
          <IconButton
            disable={plateLoader}
            className={clsx(
              classes.search__round__btn,
              plateLoader && classes.disbale__icon__search,
            )}
          >
            <SearchRoundIcon
              onClick={() =>
                !plateLoader && handlePlateLookup(plateSearchValue, unitedState)
              }
            />
          </IconButton>

          {showPlateSearchVehicles === 'plate' && (
            <ClickAwayListener
              onClickAway={() => {
                setPlateSearchVehicles([]);
              }}
            >
              <div className={classes.root__dropdown__main}>
                {plateSearchVehicles?.map((veh, key) => {
                  return (
                    <Typography
                      className={clsx(classes.dropdown__menu__text)}
                      onClick={() => {
                        onSelectVehicle(veh);
                      }}
                      key={key++}
                    >
                      {veh?.DisplayName}
                    </Typography>
                  );
                })}
              </div>
            </ClickAwayListener>
          )}
        </div>
      </div>
      <VehicleFields
        vehicleData={vehicle}
        handleChangeMcNumber={handleChangeMcNumber}
        handleChangeUnitNumber={handleChangeUnitNumber}
      />
      {/* Actions */}

      <Buttons
        onCancel={() => {
          clearState();
          setToggle(false);
        }}
        getVehicleObj={vehicleDetails}
        disabled={
          !vehicle?.vehicleMake ||
          !vehicle?.vehicleModel ||
          !vehicle?.modelYear ||
          !vehicle?.licensePlate
        }
      />
    </div>
  );
}
